import { FC, useEffect, useState } from 'react';
import { Field, useForm } from 'react-final-form';
import {
  CustomTipRow,
  CustomTipSpan,
  InputCustomTip,
  TipAmountButton,
  TipButtonsContainer,
} from './styled';
import { formatNumber } from 'helpers/utils';
import { Body, BodySmall } from 'components/Typography';
import { Row } from 'components/Row';
import { Link } from 'components/Link';
import theme from 'config/theme';
import { useStatsigClient } from '@statsig/react-bindings';
import { TIP_AMOUNTS_EXPERIMENT_NAME, useExperimentTipAmountOptions } from 'hooks/statsig';
import { mixpanelClient } from 'helpers/mixpanel';

interface Props {
  donationAmount: number;
  isVariant2: boolean;
  tipAmount: number;
  singleLine?: boolean;
}

export const Tip: FC<Props> = ({ donationAmount, isVariant2, tipAmount, singleLine = false }) => {
  const [customTipAmount, setCustomTipAmount] = useState<number | undefined>();
  const [isDonationInputVisible, setIsDonationInputVisible] = useState(false);
  const { change: changeForm } = useForm();
  const { logEvent: statsigLogEvent } = useStatsigClient();
  const { options: tipOptions, groupName: experimentGroupName } = useExperimentTipAmountOptions(
    donationAmount,
    isVariant2
  );

  useEffect(() => {
    mixpanelClient.register({
      [`${TIP_AMOUNTS_EXPERIMENT_NAME}_experiment_group_name`]: experimentGroupName,
    });
  }, [experimentGroupName]);

  useEffect(() => {
    statsigLogEvent('tip_module_exposed');
  }, []);

  useEffect(() => {
    if (tipOptions.findIndex(option => option.amount === tipAmount) < 0) {
      setCustomTipAmount(tipAmount);
    }
  }, [tipAmount]);

  useEffect(() => {
    if (isDonationInputVisible) {
      document.getElementById('custom-donate-input')?.focus();
    }
  }, [isDonationInputVisible]);

  const setTipAmount = (amount: number) => {
    changeForm('tip', amount);
  };

  const getOnTipAmountClick = (amount?: number, buttonDisplay?: string) => () => {
    statsigLogEvent('tip_option_selected', `Button ${buttonDisplay}`);
    setTipAmount(amount || 0);
  };

  const getIsSelected = (amount?: number) => {
    return !!tipAmount && amount === tipAmount;
  };

  const hideDonationInput = () => {
    setIsDonationInputVisible(false);
  };

  const toggleDonationInput = () => {
    setIsDonationInputVisible(isVisible => !isVisible);
  };

  const onCustomDonationClick = () => {
    toggleDonationInput();
    statsigLogEvent('tip_option_selected', `Button Custom Amount`);
    if (customTipAmount) {
      setTipAmount(customTipAmount);
    }
  };

  const onDonationInputChange = e => {
    if (e.target.value) {
      setCustomTipAmount(Math.round(Number.parseFloat(e.target.value) * 100) / 100);
      return;
    }
    setCustomTipAmount(undefined);
  };

  const enterCustomTip = () => {
    if (customTipAmount) {
      setTipAmount(customTipAmount);
    }
    hideDonationInput();
  };

  const onInputBlur = event => {
    if (event.relatedTarget?.id !== 'donate-amount-button') {
      enterCustomTip();
    }
  };

  const getCustomDonationDisplay = () => (customTipAmount ? formatNumber(customTipAmount) : null);

  const onRemoveTip = () => {
    setTipAmount(0);
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      event.preventDefault(); // Prevent form submission
      enterCustomTip();
    }
  };

  return (
    <>
      <Field hidden name="tip" component="input" />
      <TipButtonsContainer $singleLine={singleLine}>
        {tipOptions.map((tip, index) => (
          <TipAmountButton
            key={`${tip.amount}-${index}`}
            type="button"
            $isSelected={getIsSelected(tip.amount)}
            onClick={getOnTipAmountClick(tip.amount, tip.display)}
            $singleLine={singleLine}
          >
            <Body $color="inherit">{tip.display}</Body>
          </TipAmountButton>
        ))}
        <TipAmountButton
          id="donate-amount-button"
          type="button"
          $isSelected={getIsSelected(customTipAmount)}
          onClick={onCustomDonationClick}
          $clickableWhenSelected={true}
          $hasInput={isDonationInputVisible}
          $singleLine={singleLine}
        >
          <Body $color="inherit">
            <CustomTipRow>
              $
              {!isDonationInputVisible && (
                <CustomTipSpan $isSelected={getIsSelected(customTipAmount)}>
                  {getCustomDonationDisplay()}
                </CustomTipSpan>
              )}
              {isDonationInputVisible && (
                <InputCustomTip
                  type="number"
                  id="custom-donate-input"
                  value={customTipAmount || ''}
                  onChange={onDonationInputChange}
                  onClick={e => {
                    e.stopPropagation();
                  }}
                  onBlur={onInputBlur}
                  onKeyDown={handleKeyDown}
                />
              )}
            </CustomTipRow>
          </Body>
        </TipAmountButton>
      </TipButtonsContainer>
      <Row $justifyContent="space-around">
        <Link $color={theme.colors.shaft} onClick={onRemoveTip} type="button">
          <BodySmall $color={theme.colors.shaft}>Leave no tip</BodySmall>
        </Link>
      </Row>
    </>
  );
};
